import { DragEvent, FunctionComponent, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { faCheck, faChevronDown, faXmark } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { classNames } from "@/utils/classNames";

import { tierChipClassName } from "@/pages/Private/pages/OnboardingTour/components/steps/scoring/CriterionCard";
import { Tier } from "@/pages/Private/pages/OnboardingTour/components/steps/scoring/criteriaTiers";
import {
	decodeTierChipDrag,
	encodeTierChipDrag,
	TierChipDrag,
	TIER_CHIP_MEDIA_TYPE,
} from "@/pages/Private/pages/OnboardingTour/components/steps/scoring/tierDragDrop";

import { useViewportClampedPanel } from "@/utils/useViewportClampedPanel";

import { FixedTiers, tierOfFixedValue } from "./criteriaDraft";

interface FixedPoolSelectProps {
	tier: Tier;
	tiers: FixedTiers;
	/** Every value the criterion can grade, in display order. */
	pool: readonly string[];
	placeholder: string;
	name: string;
	onToggle: (value: string) => void;
	/** Rubric key of the criterion; set to make the chips draggable between buckets. */
	dragGroup?: string;
	/** A chip of `dragGroup` was dropped on this bucket; the payload says where from. */
	onMoveHere?: (payload: TierChipDrag) => void;
	portalContainer?: HTMLElement | null;
}

const SHORT_TIER: Record<Tier, string> = { 3: "+3", 2: "+2", 1: "+1", 0: "DQ" };

const MENU_MAX_HEIGHT = 264;

interface MenuPosition {
	top: number;
	left: number;
	width: number;
}

/**
 * The values of a fixed vocabulary (seniority levels, company-size ranges)
 * assigned to one bucket (PREC-4055). A value already in another bucket shows
 * where it sits and moves when picked here — the buckets partition the
 * vocabulary, so a value in two of them could never be graded consistently.
 *
 * The menu is portaled and positioned against the viewport (web rule §11):
 * the criterion card clips its overflow, and the Disqualify row sits at its
 * bottom edge, so an in-flow menu showed two of nine levels.
 *
 * Its chips drag between buckets exactly like the location and industry chips
 * (PREC-4079): the same private media type and payload from `tierDragDrop`, so
 * a value dragged out of one bucket lands in the one it was dropped on. Re-grading
 * a size range through the menu meant unticking it in one row and finding it
 * again in another, and every neighbouring criterion already dragged.
 */
export const FixedPoolSelect: FunctionComponent<FixedPoolSelectProps> = ({
	tier,
	tiers,
	pool,
	placeholder,
	name,
	onToggle,
	dragGroup,
	onMoveHere,
	portalContainer,
}) => {
	const { t } = useTranslation();
	const triggerRef = useRef<HTMLButtonElement>(null);
	const [open, setOpen] = useState(false);
	const menuRef = useRef<HTMLUListElement>(null);

	// Keeps the portalled panel inside the viewport wherever the trigger sits (PREC-4138).
	useViewportClampedPanel(menuRef, open);
	const [position, setPosition] = useState<MenuPosition | null>(null);
	const [isDropTarget, setIsDropTarget] = useState(false);
	const selected = tiers[tier];
	const isDraggable = dragGroup !== undefined;

	const startDrag = (event: DragEvent<HTMLElement>, value: string) => {
		if (!isDraggable) {
			return;
		}

		// The chips sit inside the trigger button: without this the drag also reads as
		// a press on it and the bucket menu opens behind the dragged chip.
		event.stopPropagation();
		event.dataTransfer.effectAllowed = "move";
		event.dataTransfer.setData(
			TIER_CHIP_MEDIA_TYPE,
			encodeTierChipDrag({ criterion: dragGroup, tier, value })
		);
	};

	// `dragover` may read the media types but never the payload, so the private type
	// is the whole check — a bucket never accepts arbitrary dragged text.
	const allowDrop = (event: DragEvent<HTMLElement>) => {
		if (!isDraggable || !event.dataTransfer.types.includes(TIER_CHIP_MEDIA_TYPE)) {
			return;
		}

		event.preventDefault();
		event.dataTransfer.dropEffect = "move";
		setIsDropTarget(true);
	};

	const drop = (event: DragEvent<HTMLElement>) => {
		event.preventDefault();
		setIsDropTarget(false);

		const payload = decodeTierChipDrag(event.dataTransfer.getData(TIER_CHIP_MEDIA_TYPE));

		if (payload && payload.criterion === dragGroup) {
			onMoveHere?.(payload);
		}
	};

	useLayoutEffect(() => {
		if (!open) {
			return;
		}

		const update = () => {
			const rect = triggerRef.current?.getBoundingClientRect();

			if (!rect) {
				return;
			}

			const fitsBelow = rect.bottom + 4 + MENU_MAX_HEIGHT <= window.innerHeight;

			setPosition({
				top: fitsBelow ? rect.bottom + 4 : Math.max(8, rect.top - 4 - MENU_MAX_HEIGHT),
				left: rect.left,
				width: Math.max(rect.width, 250),
			});
		};

		update();
		window.addEventListener("scroll", update, true);
		window.addEventListener("resize", update);

		return () => {
			window.removeEventListener("scroll", update, true);
			window.removeEventListener("resize", update);
		};
	}, [open]);

	const menu = (
		<>
			{/* Click-away layer — the menu stays open for several picks, so a document listener would fight them. */}
			<div aria-hidden="true" className="fixed inset-0 z-[1590]" onClick={() => setOpen(false)} />
			<ul
				ref={menuRef}
				className="fixed z-[1600] overflow-y-auto rounded-xl border border-border-light bg-white py-1 shadow-lg"
				data-testid={`pool-${name}-menu`}
				style={{
					top: position?.top ?? -9999,
					left: position?.left ?? -9999,
					width: position?.width,
					maxHeight: MENU_MAX_HEIGHT,
				}}
			>
				{pool.map((value) => {
					const where = tierOfFixedValue(tiers, value);
					const isHere = where === tier;

					return (
						<li key={value}>
							<button
								aria-label={value}
								aria-pressed={isHere}
								className="flex w-full cursor-pointer items-center gap-2.5 px-3 py-1.5 text-left text-sm text-text hover:bg-background-light"
								type="button"
								onClick={() => onToggle(value)}
							>
								<span
									className={classNames(
										"flex h-4 w-4 shrink-0 items-center justify-center rounded border",
										isHere ? "border-brand-light bg-brand-light text-white" : "border-gray-200"
									)}
								>
									{isHere && <Icon className="h-2 w-2" icon={faCheck} />}
								</span>
								<span className="flex-1">{value}</span>
								{where !== null && !isHere && (
									<span className="rounded-full bg-background-light px-2 py-0.5 text-[10px] font-bold text-text-meta">
										{t("leadScoring.scoringPage.inTier", { tier: SHORT_TIER[where] })}
									</span>
								)}
							</button>
						</li>
					);
				})}
			</ul>
		</>
	);

	return (
		<div className="relative min-w-0 flex-1">
			<button
				ref={triggerRef}
				aria-expanded={open}
				className={classNames(
					"input-styles flex h-auto min-h-[38px] w-full cursor-pointer flex-wrap items-center gap-1.5 rounded-lg border bg-white px-2 py-1.5 pr-8 text-left",
					isDropTarget && "border-brand-light bg-blue-action-200/30"
				)}
				data-testid={`pool-${name}`}
				type="button"
				onClick={() => setOpen((current) => !current)}
				onDragEnter={allowDrop}
				onDragLeave={() => setIsDropTarget(false)}
				onDragOver={allowDrop}
				onDrop={drop}
			>
				{selected.length === 0 ? (
					<span className="text-sm text-text-meta">{placeholder}</span>
				) : (
					selected.map((value) => (
						<span
							key={value}
							className={classNames(
								"flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold",
								isDraggable && "cursor-grab active:cursor-grabbing",
								tierChipClassName(tier)
							)}
							data-testid={`pool-${name}-chip-${value}`}
							draggable={isDraggable}
							onDragEnd={() => setIsDropTarget(false)}
							onDragStart={(event) => startDrag(event, value)}
						>
							{value}
							<span
								aria-hidden="true"
								className="cursor-pointer"
								onClick={(event) => {
									event.stopPropagation();
									onToggle(value);
								}}
							>
								<Icon className="h-3 w-3" icon={faXmark} />
							</span>
						</span>
					))
				)}
				<Icon
					className={classNames(
						"absolute right-3 top-3 h-3 w-3 text-text-meta duration-200",
						open && "rotate-180"
					)}
					icon={faChevronDown}
				/>
			</button>

			{open && createPortal(menu, portalContainer ?? document.body)}
		</div>
	);
};
